<template>
  <div>
    <v-autocomplete
      :loading='loading'
      :items='clientList'
      item-text='name_display'
      item-value='id'
      clearable
      :label="computedLabel"
      v-model='selected'
      return-object
      :search-input.sync='search'
      :hint="computedHint"
      persistent-hint
      no-data-text='No results'
      :append-outer-icon='showAdd ? "mdi-plus" : ""'
      @click:append-outer='quickCreate'
      :rules='required ? [rules.required] : []'
    ></v-autocomplete>
    <wc-quick-create-client
      ref='quickCreateDialog'
      :showQuickCreate='showQuickCreate'
      @cancel='showQuickCreate=false'
      @save='saveQuickCreate'
      @updateClient='chooseClient'
      :initialData='initialData'
    ></wc-quick-create-client>

  </div>
</template>

<script>
import formRules from '@/mixins/formRules';
import clientsAPI from '@/modules/clients/api/clients';
import WCQuickCreateClient from '@/components/clients/WCQuickCreateClient';
import _ from 'lodash';

export default {
  name: 'client-form-search',
  mixins: [formRules],
  props: {
    value: {
      required: true
    },
    label: {
      required: false,
      type: String,
      default: "Search Clients"
    },
    company: {
      required: false,
      type: Boolean,
      default: undefined
      // to search only companies, calling component sets to true. for individuals - false.  for all - leave undefined
    },
    showAdd: {
      required: false,
      type: Boolean,
      default: false
    },
    required: {
      required: false,
      type: Boolean,
      default: false
    },
    initialData: {
      required: false,
      default: null
    },
    quickpicks: {
      required: false,
      type: Array,
      default: () => []
    },
    hint: {
      required: false,
      type: String,
      default: ""
    }
  },
  data: () => ({
    loading: false,
    search: '',
    searchStatus: '',
    results: [],
    initialResults: [{header: 'Start typing to search (min 3 letters)'}],
    selected: null,
    showQuickCreate: false,
  }),
  watch: {
    'search' () {
      if (this.search && this.searchIsNotSameAsSelected) {
        const length = this.search.trim().length
        if (length > 2) {
          this.executeSearch()
        } else if (length > 0) {
          const remaining = 3 - length
          this.searchStatus = `Type ${remaining} more letter${remaining > 1 ? 's' : ''} to search`
          this.executeSearch(true)
        }
      } else if (this.search === null) {
        this.searchStatus = ''
        this.results = this.initialResults
      }
    },
    selected () {
      this.searchStatus = ''
      this.$emit('input', this.selected ? this.selected.id : null)
      this.$emit('updateQuickpicks', this.selected ? this.selected : null)
    },
    value () {
      if (this.value && !this.selected) {
        this.getClient(this.value)
        // this maintains the original value record in case the component is being used for new/edit on the same page.
        // generally, we want to clear results after each use. (so that add new always starts empty)
        // however, this will remove the client value gathered in mounted(), so we need to be prepared to re-get it in certain situations.
      }
      if (!this.value && this.selected) {
        this.selected = null
        // this will clear the value if the picker was cancelled before save. (on reopen - it'll look like something is picked, but that value will not save)
        // (the second clonedeep on re-open jostles the reactivity to the value and the main v-model breaks)
      }
    },
    'loading' () {
      this.$emit('loading', this.loading)
      // be sure to have the parent capture the loading state from here and set its own accordingly (for button disable etc.)
      // parent ----> setLoading (val) { this.isLoading = val }
    }
  },
  mounted () {
    if (this.value) {
      this.getClient(this.value)
      // this will allow the autoselect to display the current name of the person in that slot, if indeed there's a starting value.
    }
    this.results = this.initialResults
  },
  computed: {
    searchIsNotSameAsSelected () {
      if (this.selected) {
        return this.selected.name_display !== this.search
      }
      return true
    },
    computedLabel () {
      if (this.loading === true) {
        return "searching..."
      }
      return this.label
    },
    computedHint () {
      return this.searchStatus || this.hint
    },
    clientList () {
      if (this.quickpicks.length && this.results.length) {
        return _.union(_.uniq(this.quickpicks), [{divider: true}], _.uniq(this.results))
      } else if (this.quickpicks.length) {
        return this.quickpicks
      } else if (this.results.length) {
        return this.results
      } else {
        return []
      }
    }
  },
  methods: {
    executeSearch: _.debounce(function (exact) {
      this.loading = true;
      if (!exact) {
        this.searchStatus = `Searching for "${this.search.trim()}"...`
      }
      this.searchAPI(exact);
    }, 300),
    async searchAPI (exact) {
        if (this.company !== undefined){
            this.results = await clientsAPI.clientSearch(this.search.trim(), this.company, exact);
        } else {
            this.results = await clientsAPI.clientSearch(this.search.trim(), null, exact);
        }
      this.loading = false
      if (!exact) {
        this.searchStatus = (this.results.length === 0 && this.search !== '') ? `No results found for "${this.search.trim()}"` : ''
      }
    },
    quickCreate () {
      this.showQuickCreate = true
      if (this.initialData === null) {
        this.$refs.quickCreateDialog.resetForm();
      }
    },
    saveQuickCreate (newClient) {
      this.selected = newClient;
      this.results.push(this.selected);
      this.showQuickCreate = false;
    },
    clearResults () {
      this.results = []
      // if the calling component is using multiple instances of this component, it'll want to call this function and reset the form after use
    },
    async getClient(clientID) {
      this.loading = true;
      this.selected = await clientsAPI.getClients(clientID);
      this.results.push(this.selected);
      this.loading = false;
    },
    chooseClient(client){
      this.selected = client;
      this.results.push(this.selected);
      this.showQuickCreate = false;
    }
  },
  components: {
    'wc-quick-create-client': WCQuickCreateClient
  }
}
</script>